/**
 * Departments — the department view's registry and wire shapes (#1227, I1 of
 * engineering/plans/project-department-dashboard.yaml; design
 * sessions/1222/session-01/design-1222-department-dashboard.md Q1, Q3, Q4, Q5,
 * Q7, ruled by #1225).
 *
 * The read path (ruled on #1227, comment 6161142217, 2026-10-13, on Chris's
 * words of that day: no new GitHub token). The worker holds no credential
 * that reads the ten department repos. A job on the new Mac
 * (engineering/command-center/jobs/department-snapshot/) reads each repo with
 * the gh CLI's existing login and PUTs the raw GraphQL responses here as a
 * `DepartmentSnapshotPush`; the worker projects them and keeps the result.
 * `GET /api/departments/:key` answers a `DepartmentWorkResponse`: the GitHub
 * section from that kept snapshot, with its read stamp, beside the
 * department's Command Center to-dos read live from Airtable. Each section
 * carries its own state, so one failing never hides the other.
 *
 * Words on screen are the record's own coinage (plan guard): the repo's name,
 * `ticket`, the `## Type:` value as written, the `Project:` line as written,
 * the `Filed by:` / `## Origin` line under its own label. The one exception is
 * the label "Firm Success" for LegalFlare-Firm-Success, whose Tasks Component
 * value is `Firm Success` too (renamed in place from `Client Success`, #1143).
 */

import { z } from "zod";

// ---------------------------------------------------------------------------
// The registry — design Q1 (the ten live department repos) and Q4 (which
// Tasks.Component values sit under which department). A department with no
// Component value has an empty list; its tasks section says so.
// ---------------------------------------------------------------------------

export const DEPARTMENT_REPO_OWNER = "chrisfnrose";

export type Department = {
  /** URL key: the repo's suffix in lower case (design Q2). */
  key: string;
  /** Shown on screen: the repo's own name, except Firm Success (glossary). */
  label: string;
  /** The repo name under DEPARTMENT_REPO_OWNER. */
  repo: string;
  /** Tasks.Component values whose to-dos sit under this department. */
  taskComponents: readonly string[];
};

export const DEPARTMENTS: readonly Department[] = [
  { key: "builder", label: "Builder", repo: "LegalFlare-Builder", taskComponents: [] },
  { key: "data-mesh", label: "Data-Mesh", repo: "LegalFlare-Data-Mesh", taskComponents: [] },
  { key: "engineering", label: "Engineering", repo: "LegalFlare-Engineering", taskComponents: ["Engineering"] },
  { key: "executive-assistant", label: "Executive-Assistant", repo: "LegalFlare-Executive-Assistant", taskComponents: [] },
  { key: "finance", label: "Finance", repo: "LegalFlare-Finance", taskComponents: ["Finance"] },
  { key: "firm-success", label: "Firm Success", repo: "LegalFlare-Firm-Success", taskComponents: ["Firm Success"] },
  { key: "legal", label: "Legal", repo: "LegalFlare-Legal", taskComponents: ["Legal"] },
  { key: "marketing", label: "Marketing", repo: "LegalFlare-Marketing", taskComponents: ["Marketing"] },
  { key: "operations", label: "Operations", repo: "LegalFlare-Operations", taskComponents: ["Operations"] },
  { key: "teacher", label: "Teacher", repo: "LegalFlare-Teacher", taskComponents: [] },
];

export function departmentByKey(key: string): Department | null {
  return DEPARTMENTS.find((d) => d.key === key) ?? null;
}

/** "chrisfnrose/LegalFlare-<X>" — the name every failure text uses. */
export function departmentRepoFullName(d: Department): string {
  return `${DEPARTMENT_REPO_OWNER}/${d.repo}`;
}

/** The label GitHub gives an issue that /ticket works (design Q2/Q5). */
export const TICKET_LABEL = "ticket";

/** Closed items and closed to-dos: the Pacific day 6 days before the read's
 *  Pacific day, through the read (design Q5: 7 days, rolling). */
export const CLOSED_WINDOW_DAYS = 7;

// ---------------------------------------------------------------------------
// What the new-Mac job pushes — PUT /api/departments/:key/snapshot.
// One entry per GraphQL call the job made for this repo, in order: the first
// page (open issues, issues updated since the job's `since`, the repo root's
// entry names, the default branch), then any further page of open or closed
// issues. The worker reads the body; the job does no projection.
// ---------------------------------------------------------------------------

export const DepartmentSnapshotResponseSchema = z.object({
  /** Which query produced it: "first" | "open" | "closed". */
  query: z.enum(["first", "open", "closed"]),
  /** HTTP status GitHub answered with; 0 when no HTTP answer was read. */
  status: z.number().int(),
  /** The rate-limit headers GitHub sent, lower-cased names, when present. */
  headers: z.record(z.string()).default({}),
  /** The parsed JSON body, or the raw text when it did not parse. */
  body: z.unknown(),
});
export type DepartmentSnapshotResponse = z.infer<typeof DepartmentSnapshotResponseSchema>;

export const DepartmentSnapshotPushSchema = z.object({
  /** Must equal the department's repo full name. */
  repo: z.string(),
  /** ISO instant the job's read of this repo finished (the read stamp). */
  readAt: z.string(),
  /** The `since` the job passed GitHub for the updated-since list. */
  since: z.string(),
  /** Present when the job could not run a query at all (gh missing, signed
   *  out, a timeout): the job's own words, and no responses. */
  jobError: z.string().nullable().default(null),
  responses: z.array(DepartmentSnapshotResponseSchema).default([]),
  /** Where it ran — hostname and job version, for the log. */
  source: z.string().default(""),
});
export type DepartmentSnapshotPush = z.infer<typeof DepartmentSnapshotPushSchema>;

// ---------------------------------------------------------------------------
// The wire — GET /api/departments/:key.
// ---------------------------------------------------------------------------

/** Who filed it (design Q7): each line under its own label, never inferred. */
export const DepartmentFilerSchema = z.discriminatedUnion("source", [
  /** The body's `Filed by:` line, as written after the label. */
  z.object({ source: z.literal("filed_by"), text: z.string() }),
  /** The first line of the body's `## Origin` section, cut at 160 chars. */
  z.object({ source: z.literal("origin"), text: z.string() }),
  /** Neither exists in the body. */
  z.object({ source: z.literal("none") }),
]);
export type DepartmentFiler = z.infer<typeof DepartmentFilerSchema>;

export const DepartmentIssueSchema = z.object({
  number: z.number().int().positive(),
  title: z.string(),
  url: z.string(),
  createdAt: z.string(),
  labels: z.array(z.string()),
  /** Carries the `ticket` label. */
  isTicket: z.boolean(),
  /** The `## Type:` value as written, or null when the body has no such line. */
  kind: z.string().nullable(),
  /** The `Project:` line's value as written, or null. */
  project: z.string().nullable(),
  filer: DepartmentFilerSchema,
});
export type DepartmentIssue = z.infer<typeof DepartmentIssueSchema>;

/** What happened on a closed issue (design Q5), first match wins. */
export const DepartmentWhatHappenedSchema = z.discriminatedUnion("source", [
  /** The latest `Close summary (/stop)` comment: its What line and links. */
  z.object({
    source: z.literal("close_summary"),
    what: z.string(),
    pr: z.string().nullable(),
    record: z.string().nullable(),
    commentedAt: z.string(),
  }),
  /** The latest /ticket close comment (opens `Needed:`, has `Done:`). */
  z.object({
    source: z.literal("ticket_done"),
    done: z.string(),
    where: z.string().nullable(),
    commentedAt: z.string(),
  }),
  /** Neither: what the view observed — the close reason as GitHub gives it,
   *  and the last comment's first line (null when the issue has none). */
  z.object({
    source: z.literal("observed"),
    closeReason: z.string().nullable(),
    lastComment: z
      .object({ createdAt: z.string(), firstLine: z.string() })
      .nullable(),
  }),
]);
export type DepartmentWhatHappened = z.infer<typeof DepartmentWhatHappenedSchema>;

export const DepartmentClosedIssueSchema = DepartmentIssueSchema.extend({
  closedAt: z.string(),
  /** GitHub's stateReason exactly as given (COMPLETED, NOT_PLANNED, …). */
  stateReason: z.string().nullable(),
  /** Whether `closeout-log-<N>.yaml` sits at the repo root on the default
   *  branch at the read, and its link when it does. */
  closeoutLog: z.object({
    name: z.string(),
    present: z.boolean(),
    url: z.string().nullable(),
  }),
  whatHappened: DepartmentWhatHappenedSchema,
});
export type DepartmentClosedIssue = z.infer<typeof DepartmentClosedIssueSchema>;

export const DepartmentWindowSchema = z.object({
  /** First Pacific day in the window (YYYY-MM-DD). */
  start: z.string(),
  /** Last Pacific day in the window — the read's own day (YYYY-MM-DD). */
  end: z.string(),
});

export const DepartmentGithubSectionSchema = z.discriminatedUnion("state", [
  z.object({
    state: z.literal("read"),
    /** When the new-Mac job finished reading the repo. */
    readAt: z.string(),
    /** When the snapshot reached Command Center. */
    receivedAt: z.string(),
    defaultBranch: z.string(),
    window: DepartmentWindowSchema,
    /** Open issues labelled `ticket`, highest number first. */
    openTickets: z.array(DepartmentIssueSchema),
    /** Open issues not labelled `ticket`, highest number first. */
    openIssues: z.array(DepartmentIssueSchema),
    /** Issues (tickets included) closed in the window, newest close first. */
    closed: z.array(DepartmentClosedIssueSchema),
  }),
  z.object({
    state: z.literal("failed"),
    attemptedAt: z.string(),
    receivedAt: z.string(),
    /** The observation shown on screen (design Q3's failure texts). */
    text: z.string(),
  }),
  z.object({
    state: z.literal("absent"),
    text: z.string(),
  }),
]);
export type DepartmentGithubSection = z.infer<typeof DepartmentGithubSectionSchema>;

export const DepartmentTaskSchema = z.object({
  id: z.string(),
  title: z.string(),
  /** Tasks.Status as written: open | in progress | done. */
  status: z.string(),
  assignee: z.string().nullable(),
  dueBy: z.string().nullable(),
  snoozedUntil: z.string().nullable(),
  /** Tasks.Component as written (e.g. "Firm Success"). */
  component: z.string().nullable(),
  /** The free-text Reference when the task is about nothing linked. */
  reference: z.string().nullable(),
  created: z.string(),
});
export type DepartmentTask = z.infer<typeof DepartmentTaskSchema>;

export const DepartmentClosedTaskSchema = DepartmentTaskSchema.extend({
  /** Tasks.Completed (a date-only field), as written. */
  completed: z.string(),
});
export type DepartmentClosedTask = z.infer<typeof DepartmentClosedTaskSchema>;

export const DepartmentTasksSectionSchema = z.discriminatedUnion("state", [
  z.object({
    state: z.literal("read"),
    readAt: z.string(),
    components: z.array(z.string()),
    window: DepartmentWindowSchema,
    /** Live to-dos (Completed blank, an assignee set), snoozed ones included. */
    open: z.array(DepartmentTaskSchema),
    /** To-dos whose Completed falls in the window, newest first. */
    closed: z.array(DepartmentClosedTaskSchema),
  }),
  z.object({
    state: z.literal("failed"),
    attemptedAt: z.string(),
    text: z.string(),
  }),
  /** The department has no Tasks.Component value (design Q4). */
  z.object({
    state: z.literal("none"),
    text: z.string(),
  }),
]);
export type DepartmentTasksSection = z.infer<typeof DepartmentTasksSectionSchema>;

export const DepartmentWorkResponseSchema = z.object({
  department: z.object({
    key: z.string(),
    label: z.string(),
    repo: z.string(),
    url: z.string(),
  }),
  github: DepartmentGithubSectionSchema,
  tasks: DepartmentTasksSectionSchema,
});
export type DepartmentWorkResponse = z.infer<typeof DepartmentWorkResponseSchema>;

/** PUT /api/departments/:key/snapshot's answer — what was kept. */
export const DepartmentSnapshotAckSchema = z.object({
  key: z.string(),
  state: z.enum(["read", "failed"]),
  readAt: z.string(),
  openTickets: z.number().int(),
  openIssues: z.number().int(),
  closed: z.number().int(),
});
export type DepartmentSnapshotAck = z.infer<typeof DepartmentSnapshotAckSchema>;
